import type { Dayjs } from 'dayjs'
import dayjs from '@/lib/dayjs'
import type {
  SalesSummaryReportActivityRow,
  SalesSummaryReportProductRow,
  SalesSummaryReportSummary,
  SalesSummaryReportTimeSeriesBucket,
} from '@/lib/services/reports/reports.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import { REPORT_PIE_CHART_COLORS } from '../-helpers'

// The sales-statistics report fields these helpers read (GET /reports/sales-summary).

export interface SalesStatisticsCustomerRow {
  has_customer_information?: boolean | null
  purchaser_customer_id?: number | null
  customer_id?: number | null
  customer_name?: string | null
}

/** A row of a sales breakdown (by fulfillment type, channel, and so on). */
export type SalesStatisticsBreakdownRow = Pick<
  SalesSummaryReportActivityRow,
  'label' | 'total_sales' | 'order_count'
>

export type SalesStatisticsProductRow = Pick<
  SalesSummaryReportProductRow,
  'sold_total' | 'refunded_total' | 'net_total'
>

export interface SalesStatisticsReport {
  summary: Pick<SalesSummaryReportSummary, 'net_sales' | 'paid_order_count'>
  time_series: Pick<SalesSummaryReportTimeSeriesBucket, 'net_sales' | 'paid_order_count'>[]
}

/** A time-series bucket boundary: a date or datetime string, or a timestamp. */
type BucketValue = string | number | null | undefined

export interface SalesStatisticsBucket {
  bucket_start?: BucketValue
  bucket_end?: BucketValue
}

/** A chart data point; the axis helpers read numeric series by key. */
export type ChartDatum = Record<string, unknown>

export interface ChartAxisTick {
  fill: string
  fontSize: number
  fontWeight: number
}

export interface BreakdownChartFields {
  chartKey: string
  chart_value: number
  fill: string | undefined
  share: number
}

type BreakdownChartRowInput = Pick<SalesStatisticsBreakdownRow, 'label'> &
  Pick<BreakdownChartFields, 'fill' | 'share'>

export interface BreakdownDisplayFields {
  order_share: number
  sales_share: number
  accentColor: string | undefined
}

type NumberRange = [number, number]

export interface CombinedChartAxis {
  domain: NumberRange
  ticks: number[]
}

export interface BucketedChartAxisDomains {
  positive: NumberRange
  negative: NumberRange
}

export const ALL_FILTER_VALUE = 'all'
export const MISSING_CUSTOMER_LABEL = 'None provided'

export function getCustomerToneKey(
  customerId: number | string | null | undefined,
  customerName: string | null | undefined
): number | string {
  return customerId ?? customerName ?? ''
}

export function hasProvidedCustomer(row: SalesStatisticsCustomerRow | null | undefined): boolean {
  if (typeof row?.has_customer_information === 'boolean') {
    return row.has_customer_information
  }

  if (row?.purchaser_customer_id || row?.customer_id) {
    return true
  }

  return Boolean(String(row?.customer_name ?? '').trim())
}

export function getCustomerDisplayName(row: SalesStatisticsCustomerRow | null | undefined): string {
  return hasProvidedCustomer(row) ? String(row?.customer_name ?? '').trim() : MISSING_CUSTOMER_LABEL
}

export function getChartAxisTick(isMobile: boolean): ChartAxisTick {
  return {
    fill: 'var(--color-foreground)',
    fontSize: isMobile ? 10 : 11,
    fontWeight: 500,
  }
}

export function buildBreakdownChartRows<Row extends SalesStatisticsBreakdownRow>(
  rows: Row[]
): (Row & BreakdownChartFields)[] {
  const activityRows = rows
    .filter((row) => getSignedActivityAmount(row) !== 0)
    .sort(compareRowsByActivity)
  const totalActivity = activityRows.reduce(
    (sum, row) => sum + Math.abs(getSignedActivityAmount(row)),
    0
  )

  if (totalActivity <= 0) {
    return []
  }

  return activityRows.map((row, index) => ({
    ...row,
    chartKey: `breakdown-${index + 1}`,
    chart_value: Math.abs(getSignedActivityAmount(row)),
    fill: REPORT_PIE_CHART_COLORS[index % REPORT_PIE_CHART_COLORS.length],
    share: Math.abs(getSignedActivityAmount(row)) / totalActivity,
  }))
}

// Workload views size each slice by orders placed rather than dollars.
export function buildOrderCountChartRows<Row extends SalesStatisticsBreakdownRow>(
  rows: Row[]
): (Row & BreakdownChartFields)[] {
  const countedRows = rows
    .filter((row) => Number(row.order_count ?? 0) > 0)
    .sort((left, right) => Number(right.order_count) - Number(left.order_count))
  const totalOrders = countedRows.reduce((sum, row) => sum + Number(row.order_count), 0)

  return countedRows.map((row, index) => ({
    ...row,
    chartKey: `breakdown-${index + 1}`,
    chart_value: Number(row.order_count),
    fill: REPORT_PIE_CHART_COLORS[index % REPORT_PIE_CHART_COLORS.length],
    share: Number(row.order_count) / totalOrders,
  }))
}

export function buildBreakdownDisplayRows<Row extends SalesStatisticsBreakdownRow>(
  rows: Row[],
  chartRows: BreakdownChartRowInput[]
): (Row & BreakdownDisplayFields)[] {
  const totalOrderCount = rows.reduce((sum, row) => sum + Number(row.order_count ?? 0), 0)
  const chartRowByLabel = new Map(chartRows.map((row) => [row.label, row]))
  const chartOrderByLabel = new Map(chartRows.map((row, index) => [row.label, index]))

  return rows
    .map((row) => {
      const orderCount = Number(row.order_count ?? 0)
      const chartRow = chartRowByLabel.get(row.label)

      return {
        ...row,
        order_share: totalOrderCount > 0 ? orderCount / totalOrderCount : 0,
        sales_share: chartRow?.share ?? 0,
        accentColor: chartRow?.fill,
      }
    })
    .sort((left, right) => {
      const leftOrder = chartOrderByLabel.get(left.label)
      const rightOrder = chartOrderByLabel.get(right.label)

      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder
      }

      if (leftOrder !== undefined) {
        return -1
      }

      if (rightOrder !== undefined) {
        return 1
      }

      return compareRowsByActivity(left, right)
    })
}

function getSignedActivityAmount(row: SalesStatisticsBreakdownRow | null | undefined): number {
  return parseCurrency(row?.total_sales)
}

function compareRowsByActivity(
  left: SalesStatisticsBreakdownRow,
  right: SalesStatisticsBreakdownRow
): number {
  const activityDifference =
    Math.abs(getSignedActivityAmount(right)) - Math.abs(getSignedActivityAmount(left))

  if (activityDifference !== 0) {
    return activityDifference
  }

  return getSignedActivityAmount(right) - getSignedActivityAmount(left)
}

export function getProductActivityAmount(
  row: SalesStatisticsProductRow | null | undefined
): number {
  return Math.abs(parseCurrency(row?.sold_total)) + Math.abs(parseCurrency(row?.refunded_total))
}

export function getProductSignedAmount(row: SalesStatisticsProductRow | null | undefined): number {
  return parseCurrency(row?.net_total)
}

export function hasSalesSummaryActivity(report: SalesStatisticsReport | null | undefined): boolean {
  const netSales = parseCurrency(report?.summary?.net_sales)
  const paidOrderCount = Number(report?.summary?.paid_order_count ?? 0)
  const hasTimeSeriesActivity = (report?.time_series ?? []).some(
    (bucket) => Number(bucket?.paid_order_count ?? 0) > 0 || parseCurrency(bucket?.net_sales) !== 0
  )

  return paidOrderCount > 0 || netSales !== 0 || hasTimeSeriesActivity
}

export function truncateChartLabel(value: string | null | undefined, maxLength = 22): string {
  if (!value) {
    return ''
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
}

export function formatQuantity(value: number | string | null | undefined): string {
  const number = Number(value ?? 0)

  if (Number.isInteger(number)) {
    return number.toLocaleString()
  }

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

export function formatCurrencyAxis(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0'
  }

  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 1,
    }).format(value)
  }

  return formatCurrency(value)
}

export function getAreaChartTickInterval(
  bucketCount: number,
  granularity: string,
  isMobile = false
): number {
  if (bucketCount <= 1) {
    return 0
  }

  const targetTickCount = isMobile
    ? granularity === 'monthly'
      ? 4
      : granularity === 'weekly'
        ? 4
        : granularity === 'hourly'
          ? 6
          : 3
    : granularity === 'monthly'
      ? 12
      : granularity === 'weekly'
        ? 8
        : granularity === 'hourly'
          ? 12
          : 6

  return Math.max(0, Math.ceil(bucketCount / targetTickCount) - 1)
}

export function buildCombinedChartAxis(
  data: (ChartDatum | null | undefined)[],
  runningTotalKey: string,
  bucketedDataKey: string
): CombinedChartAxis {
  const runningValues = data.map((row) => readChartAmount(row, runningTotalKey))
  const bucketedValues = data.map((row) => readChartAmount(row, bucketedDataKey))
  const minValue = Math.min(...runningValues, ...bucketedValues, 0)
  const maxValue = Math.max(...runningValues, ...bucketedValues, 0)
  const domainMin = minValue < 0 ? normalizeNegativeDomainFloor(minValue) : 0
  const domainMax = maxValue > 0 ? maxValue : 1
  const positiveStep = domainMax / 4 || 1
  const totalRange = domainMax - domainMin || 1
  const positiveShare = domainMax / totalRange

  if (domainMin < 0 && positiveShare <= 0.08) {
    return {
      domain: [domainMin, domainMax],
      ticks: [domainMax, domainMin],
    }
  }

  if (domainMin < 0 && positiveShare <= 0.18) {
    return {
      domain: [domainMin, domainMax],
      ticks: [domainMax, 0, domainMin],
    }
  }

  return {
    domain: [domainMin, domainMax],
    ticks: Array.from({ length: 5 }, (_, index) => positiveStep * index),
  }
}

export function buildBucketedChartAxisDomains(
  data: (ChartDatum | null | undefined)[],
  bucketedDataKey: string,
  zeroAlignedDomain: readonly number[] | null | undefined
): BucketedChartAxisDomains {
  const bucketedValues = data.map((row) => readChartAmount(row, bucketedDataKey))
  const minValue = Math.min(...bucketedValues, 0)
  const maxValue = Math.max(...bucketedValues, 0)
  const zeroRatio = getZeroRatio(zeroAlignedDomain)

  if (minValue === 0 && maxValue === 0) {
    return {
      positive: [0, 1],
      negative: [-1, 0],
    }
  }

  const positiveMax = maxValue > 0 ? maxValue * 3 : 1
  const negativeMax = minValue < 0 ? Math.abs(minValue) * 1.2 : 1

  return {
    positive: buildZeroAlignedPositiveDomain(positiveMax, zeroRatio),
    negative: buildZeroAlignedNegativeDomain(negativeMax, zeroRatio),
  }
}

// Chart rows carry parsed amounts; an API money string reads the same way.
function readChartAmount(row: ChartDatum | null | undefined, key: string): number {
  const value = row?.[key]

  return typeof value === 'number' || typeof value === 'string' ? parseCurrency(value) : 0
}

function getZeroRatio(domain: readonly number[] | null | undefined): number {
  const [domainMin = 0, domainMax = 1] = domain ?? []
  const range = domainMax - domainMin

  if (range <= 0 || domainMax <= 0) {
    return 1
  }

  return Math.min(1, Math.max(0, domainMax / range))
}

function buildZeroAlignedPositiveDomain(positiveMax: number, zeroRatio: number): NumberRange {
  if (zeroRatio >= 1) {
    return [0, positiveMax]
  }

  if (zeroRatio <= 0) {
    return [0, positiveMax]
  }

  return [-(positiveMax * (1 - zeroRatio)) / zeroRatio, positiveMax]
}

function buildZeroAlignedNegativeDomain(negativeMax: number, zeroRatio: number): NumberRange {
  if (zeroRatio >= 1) {
    return [-negativeMax, 0]
  }

  if (zeroRatio <= 0) {
    return [-negativeMax, 0]
  }

  return [-negativeMax, (negativeMax * zeroRatio) / (1 - zeroRatio)]
}

function normalizeNegativeDomainFloor(value: number): number {
  const absolute = Math.abs(value)

  if (absolute >= 1000) {
    return -Math.ceil(absolute / 500) * 500
  }

  if (absolute >= 100) {
    return -Math.ceil(absolute / 50) * 50
  }

  if (absolute >= 10) {
    return -Math.ceil(absolute / 10) * 10
  }

  return -Math.ceil(absolute)
}

function parseBucketValue(value: BucketValue, timezone: string | null | undefined): Dayjs {
  if (typeof value === 'string' && timezone && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return dayjs.tz(value, timezone)
  }

  const parsed = dayjs(value)

  if (!parsed.isValid()) {
    return parsed
  }

  return timezone ? parsed.tz(timezone) : parsed
}

export function formatBucketAxisLabel(
  value: BucketValue,
  granularity: string,
  timezone?: string | null
): string {
  const parsed = parseBucketValue(value, timezone)

  if (!parsed.isValid()) {
    return String(value ?? '')
  }

  if (granularity === 'monthly') {
    return parsed.format('MMM')
  }

  if (granularity === 'hourly') {
    return parsed.format('h A')
  }

  return parsed.format('MMM D')
}

export function formatBucketTooltipLabel(
  bucket: SalesStatisticsBucket | null | undefined,
  granularity: string,
  timezone?: string | null
): string {
  const start = parseBucketValue(bucket?.bucket_start, timezone)
  const end = parseBucketValue(bucket?.bucket_end, timezone)

  if (!start.isValid()) {
    return ''
  }

  if (granularity === 'monthly') {
    return start.format('MMMM YYYY')
  }

  if (granularity === 'hourly' && end.isValid()) {
    return `${start.format('ddd MMM D, YYYY h:mm A')} - ${end.format('h:mm A')}`
  }

  if (granularity === 'weekly' && end.isValid()) {
    return start.isSame(end, 'month')
      ? `${start.format('MMM D')} - ${end.format('D')}`
      : `${start.format('MMM D')} - ${end.format('MMM D')}`
  }

  return start.format('ddd MMM D, YYYY')
}
